import { useEffect, useRef, useState } from "react";
import api, { API, tokenStore } from "@/lib/api";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { toast } from "sonner";

// fire-and-forget release that survives tab close / navigation
const releaseNow = (id) =>
  fetch(`${API}/inquiries/${id}/release`, {
    method: "POST",
    keepalive: true,
    headers: { Authorization: `Bearer ${tokenStore.access}` },
  }).catch(() => {});

export function RespondModal({ inquiry, onClose, onSubmitted, onReleased }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const done = useRef(false);
  const pendingRelease = useRef(null);
  const words = countWords(text);

  useEffect(() => {
    // React StrictMode (dev) mounts → unmounts → remounts effects; defer the unmount release
    // one tick so a simulated remount can cancel it instead of freeing the claim immediately.
    if (pendingRelease.current) { clearTimeout(pendingRelease.current); pendingRelease.current = null; }
    const onHide = () => { if (!done.current) { done.current = true; releaseNow(inquiry.id); } };
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      pendingRelease.current = setTimeout(onHide, 0);
    };
  }, [inquiry.id]);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/inquiries/${inquiry.id}/responses`, { text });
      done.current = true;
      toast.success("Answer submitted");
      onSubmitted(inquiry.id);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (busy) return;
    if (!done.current) {
      done.current = true;
      await releaseNow(inquiry.id);
      onReleased(inquiry.id);
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={close}
      data-testid="respond-modal-overlay"
    >
      <div
        className="w-full max-w-xl p-8 my-auto"
        style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="respond-modal"
      >
        <div className="flex justify-between items-start mb-6">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--accent)" }}>
            held while open · closing returns it to the pile
          </span>
          <button onClick={close} disabled={busy} data-testid="respond-modal-cancel-top" className="text-sm opacity-60 hover:opacity-100 shrink-0 ml-4" style={{ color: "var(--text-primary)" }}>
            [ close ]
          </button>
        </div>

        <p className="long-text font-head font-light text-xl mb-8 leading-relaxed" style={{ color: "var(--text-primary)" }}>
          {inquiry.text}
        </p>

        <textarea
          data-testid="respond-textarea"
          value={text}
          onChange={(e) => setText(limitWords(e.target.value, text, 1000))}
          rows={5}
          placeholder="type your answer…"
          className="w-full p-4 text-base resize-none"
          style={{ background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
        />
        <div className="text-right mt-2 text-xs tracking-widest" style={{ color: words >= 1000 ? "var(--accent)" : "var(--text-muted)" }} data-testid="respond-word-count">
          {words.toLocaleString()} / 1,000 words
        </div>

        <button
          onClick={submit}
          disabled={busy || !text.trim()}
          data-testid="respond-submit-btn"
          className="btn w-full py-3 mt-6 text-sm tracking-widest"
        >
          {busy ? "…" : "SUBMIT ANSWER"}
        </button>
      </div>
    </div>
  );
}
